import { useState } from "react";
import {
  Button,
  Form,
  InputGroup,
  OverlayTrigger,
  Table,
  Tooltip,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faObjectGroup,
  faObjectUngroup,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { useFormContext } from "./formElements";
import CustomDialog from "../CustomDialog";
import "./TableSelect.css";

// dopasowanie wysokości textarea do treści (fallback gdy brak wsparcia field-sizing)
const autoResize = (el) => {
  if (!el || el.scrollHeight <= 0) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight + 2}px`;
};

function CellInput({ className = "", ...props }) {
  return (
    <Form.Control
      as="textarea"
      rows={1}
      ref={autoResize}
      onInput={(event) => autoResize(event.target)}
      className={`border-0 bg-transparent table-select-cell ${className}`}
      {...props}
    />
  );
}

// etykieta nagłówka z opcjonalnym checkboxem widoczności w PDF
function HeaderLabel({ label, hideInPdf, className, onChangeLabel, onChangeVisible }) {
  const labelInput = (
    <CellInput
      className={className}
      value={label}
      onChange={(event) => onChangeLabel(event.target.value)}
    />
  );

  if (hideInPdf === undefined) return labelInput;

  return (
    <InputGroup className="flex-nowrap">
      {labelInput}
      <OverlayTrigger
        placement={"top"}
        overlay={
          <Tooltip>{hideInPdf ? "niewidoczne w PDF" : "widoczne w PDF"}</Tooltip>
        }
      >
        <InputGroup.Text className={"border-0 bg-transparent px-1"}>
          <Form.Check
            type={"checkbox"}
            className={"m-0"}
            checked={!hideInPdf}
            onChange={(event) => onChangeVisible(event.target.checked)}
          />
        </InputGroup.Text>
      </OverlayTrigger>
    </InputGroup>
  );
}

// zakres kolumn danych [start, end) pokrywany przez każdą komórkę nagłówka (uwzględnia rowspan/colspan)
const headerColumnRanges = (header) => {
  const occupied = header.map(() => []);

  return header.map((headerRow, rowIndex) => {
    let col = 0;
    return headerRow.map((cell) => {
      while (occupied[rowIndex][col]) col++;

      const colspan = cell.colspan ?? 1;
      const rowspan = cell.rowspan ?? 1;
      for (let r = rowIndex; r < Math.min(rowIndex + rowspan, header.length); r++)
        for (let c = col; c < col + colspan; c++) occupied[r][c] = true;

      const range = [col, col + colspan];
      col += colspan;
      return range;
    });
  });
};

// pusta komórka wiersza na podstawie definicji kolumny modelu
const cellFromColumn = (column) => {
  const { hideInPdf, ...cell } = column;
  return { ...cell };
};

// grupa nagłówka dla każdej kolumny danych - komórka nagłówka sięgająca ostatniego wiersza
// nagłówka nad daną kolumną; scalać można tylko w obrębie jednej grupy.
// Tabela bez złożonego nagłówka nie ma grup (null) - scalanie bez ograniczeń.
const columnGroups = (header, columnsCount) => {
  const groups = new Array(columnsCount).fill(null);
  if (!Array.isArray(header) || header.length === 0) return groups;

  const ranges = headerColumnRanges(header);
  header.forEach((headerRow, rowIndex) =>
    headerRow.forEach((cell, cellIndex) => {
      if (rowIndex + (cell.rowspan ?? 1) < header.length) return;
      const [start, end] = ranges[rowIndex][cellIndex];
      for (let c = start; c < end && c < columnsCount; c++)
        groups[c] = `${rowIndex}-${cellIndex}`;
    })
  );
  return groups;
};

// przepisuje wiersz na kolumny nowego modelu - wartości kolumn o tej samej nazwie zostają,
// scalenia (colspan/merged) są kasowane
const convertRow = (row, columns) => {
  return columns.map((column) => {
    const existing = row.find((cell) => cell.name === column.name);
    return existing
      ? { ...cellFromColumn(column), value: existing.value }
      : cellFromColumn(column);
  });
};

// czy zmiana modelu usunie wpisane dane (kolumny, których nie ma w nowym modelu)
const willLoseData = (rows, oldColumns, newColumns) => {
  const newNames = newColumns.map((c) => c.name);
  return rows.some((row) =>
    row.some((cell) => {
      if (newNames.includes(cell.name)) return false;
      const def = oldColumns.find((c) => c.name === cell.name);
      return cell.value !== "" && cell.value !== (def?.value ?? "");
    })
  );
};

export default function TableSelect(props) {
  const element = props.value;
  const { onChange } = useFormContext();
  const [pendingKey, setPendingKey] = useState(null);

  const models = element.models ?? [];
  const model = models.find((m) => m.key === element.selected) ?? models[0];
  const values = element.value ?? [];

  if (!model) return null;

  const columns = model.columns;
  const header = model.header;

  const applyModel = (key) => {
    const next = models.find((m) => m.key === key);
    if (!next) return;

    element.selected = key;
    element.value = values.map((row) => convertRow(row, next.columns));
    onChange({ ...element });
  };

  const handleSelectModel = (event) => {
    const key = event.target.value;
    const next = models.find((m) => m.key === key);
    if (!next || key === model.key) return;

    if (willLoseData(values, columns, next.columns)) setPendingKey(key);
    else applyModel(key);
  };

  const groups = columnGroups(header, columns.length);

  // komórka może scalić się z prawą, gdy nie jest zakryta, ma sąsiada po prawej
  // i cały nowy zakres leży w jednej grupie nagłówka
  const canMergeRight = (row, cellIndex) => {
    const cell = row[cellIndex];
    if (cell.merged) return false;
    const next = cellIndex + (cell.colspan ?? 1);
    if (next >= row.length) return false;
    const last = next + (row[next].colspan ?? 1) - 1;
    return groups[cellIndex] === groups[last];
  };

  const handleMergeRight = (rowIndex, cellIndex) => {
    const row = [...values[rowIndex]];
    const cell = row[cellIndex];
    const next = cellIndex + (cell.colspan ?? 1);
    const nextCell = row[next];
    const span = (cell.colspan ?? 1) + (nextCell.colspan ?? 1);

    row[cellIndex] = {
      ...cell,
      value: cell.value !== "" ? cell.value : nextCell.value,
      colspan: span,
    };
    for (let c = cellIndex + 1; c < cellIndex + span; c++) {
      const { colspan, ...rest } = row[c];
      row[c] = { ...rest, value: "", merged: true };
    }

    element.value[rowIndex] = row;
    onChange({ ...element });
  };

  const handleSplit = (rowIndex, cellIndex) => {
    const row = [...values[rowIndex]];
    const { colspan, ...start } = row[cellIndex];
    row[cellIndex] = start;
    for (let c = cellIndex + 1; c < cellIndex + (colspan ?? 1) && c < row.length; c++) {
      const { merged, ...rest } = row[c];
      row[c] = rest;
    }

    element.value[rowIndex] = row;
    onChange({ ...element });
  };

  const handleAddRow = () => {
    element.value = [...values, columns.map(cellFromColumn)];
    onChange({ ...element });
  };

  const handleDeleteRow = (index) => {
    element.value.splice(index, 1);
    onChange({ ...element });
  };

  const handleChangeCell = (rowIndex, cellIndex, value) => {
    const row = values[rowIndex];
    row[cellIndex] = { ...row[cellIndex], value: value };
    element.value[rowIndex] = row;
    onChange(element);
  };

  const handleChangeColumn = (colIndex, changes) => {
    model.columns[colIndex] = { ...model.columns[colIndex], ...changes };

    // etykieta w wierszach trzymana jest też w komórkach - utrzymujemy spójność
    if (changes.label !== undefined) {
      const name = model.columns[colIndex].name;
      element.value = values.map((row) =>
        row.map((cell) =>
          cell.name === name ? { ...cell, label: changes.label } : cell
        )
      );
    }

    onChange({ ...element });
  };

  const handleChangeHeaderCell = (rowIndex, cellIndex, label) => {
    header[rowIndex][cellIndex] = { ...header[rowIndex][cellIndex], label };
    onChange({ ...element });
  };

  // ukrycie komórki nagłówka ukrywa wszystkie kolumny pod nią; pozostałe komórki
  // nagłówka są przeliczane - ukryte gdy wszystkie ich kolumny są ukryte
  const handleChangeHeaderVisible = (rowIndex, cellIndex, visible) => {
    const ranges = headerColumnRanges(header);
    const [start, end] = ranges[rowIndex][cellIndex];

    for (let c = start; c < end && c < model.columns.length; c++)
      model.columns[c] = { ...model.columns[c], hideInPdf: !visible };

    header.forEach((headerRow, r) =>
      headerRow.forEach((cell, i) => {
        if (cell.hideInPdf === undefined) return;
        const [s, e] = ranges[r][i];
        const covered = model.columns.slice(s, e);
        header[r][i] = {
          ...cell,
          hideInPdf: covered.length > 0 && covered.every((col) => col.hideInPdf),
        };
      })
    );

    onChange({ ...element });
  };

  const renderSimpleHeader = () => (
    <tr>
      <th className="text-center align-middle">L.p.</th>
      {columns.map((col, i) => (
        <th key={i} className="p-0">
          <HeaderLabel
            className={"fw-bold"}
            label={col.label}
            hideInPdf={col.hideInPdf}
            onChangeLabel={(label) => handleChangeColumn(i, { label })}
            onChangeVisible={(visible) =>
              handleChangeColumn(i, { hideInPdf: !visible })
            }
          />
        </th>
      ))}
      <th></th>
    </tr>
  );

  const renderGroupedHeader = () =>
    header.map((headerRow, rowIndex) => (
      <tr key={rowIndex}>
        {rowIndex === 0 && (
          <th className="text-center align-middle" rowSpan={header.length}>
            L.p.
          </th>
        )}
        {headerRow.map((cell, cellIndex) => (
          <th
            key={cellIndex}
            className="p-0 align-middle"
            colSpan={cell.colspan ?? 1}
            rowSpan={cell.rowspan ?? 1}
          >
            <HeaderLabel
              className={"fw-bold text-center"}
              label={cell.label}
              hideInPdf={cell.hideInPdf}
              onChangeLabel={(label) =>
                handleChangeHeaderCell(rowIndex, cellIndex, label)
              }
              onChangeVisible={(visible) =>
                handleChangeHeaderVisible(rowIndex, cellIndex, visible)
              }
            />
          </th>
        ))}
        {rowIndex === 0 && <th rowSpan={header.length}></th>}
      </tr>
    ));

  return (
    <div className="mt-3 m-2">
      <CustomDialog
        show={pendingKey !== null}
        title="Zmiana tabeli"
        content="Nowa tabela nie ma części kolumn - wpisane w nich dane zostaną usunięte. Kontynuować?"
        onClose={() => setPendingKey(null)}
        confirmButtonText="Zmień"
        onConfirm={() => {
          applyModel(pendingKey);
          setPendingKey(null);
        }}
        cancelButtonText="Anuluj"
      />
      <div className="d-flex align-items-center gap-2 mb-1">
        <Form.Select
          className="w-auto"
          aria-label="Wybierz rodzaj tabeli"
          value={model.key}
          onChange={handleSelectModel}
        >
          {models.map((m) => (
            <option key={m.key} value={m.key}>
              {m.name}
            </option>
          ))}
        </Form.Select>
      </div>
      <div className="aeration_table">
        <Table striped bordered hover size="sm" className="table-select-table">
          <thead>
            {Array.isArray(header) && header.length > 0
              ? renderGroupedHeader()
              : renderSimpleHeader()}
          </thead>
          <tbody>
            {values.map((row, rowIndex) => (
              <tr key={rowIndex}>
                <td className="text-center align-middle">{rowIndex + 1}</td>
                {row.map((cell, cellIndex) => {
                  if (cell.merged) return null;

                  const isMerged = (cell.colspan ?? 1) > 1;
                  return (
                    <td
                      className={"p-0 table-select-td"}
                      key={cellIndex}
                      colSpan={cell.colspan ?? 1}
                    >
                      <CellInput
                        className={isMerged ? "text-center" : ""}
                        value={cell.value ?? ""}
                        placeholder={cell.label}
                        onChange={(event) =>
                          handleChangeCell(rowIndex, cellIndex, event.target.value)
                        }
                      />
                      <div className="table-select-cell-actions">
                        {isMerged && (
                          <button
                            type="button"
                            title="Rozdziel komórki"
                            onClick={() => handleSplit(rowIndex, cellIndex)}
                          >
                            <FontAwesomeIcon icon={faObjectUngroup} />
                          </button>
                        )}
                        {canMergeRight(row, cellIndex) && (
                          <button
                            type="button"
                            title="Scal z komórką po prawej"
                            onClick={() => handleMergeRight(rowIndex, cellIndex)}
                          >
                            <FontAwesomeIcon icon={faObjectGroup} />
                          </button>
                        )}
                      </div>
                    </td>
                  );
                })}
                <td className="align-middle text-center">
                  <div
                    className={"btn-close"}
                    title="Usuń wiersz"
                    onClick={() => handleDeleteRow(rowIndex)}
                  ></div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <Button variant={"outline-secondary"} onClick={handleAddRow}>
        <FontAwesomeIcon icon={faPlus} /> dodaj wiersz
      </Button>
    </div>
  );
}
